// Home-page strip: live Booking.com score + the latest 9+ guest quotes (GET /api/public/booking-reviews, read nightly — same data as the Beds24 page).
import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Quote } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { hotelInfo } from '../data/mock';

const API = process.env.REACT_APP_BACKEND_URL;
const PILL_LANG = { en: 'en', me: 'sr', sq: 'sq', de: 'de', ru: 'ru', pl: 'pl', hu: 'hu' };
const T = {
  en: { title: 'What guests said this season', sub: 'Latest 9+ reviews on Booking.com, updated nightly — unedited', all: 'Read all reviews on Booking.com', ours: 'Our reviews' },
  me: { title: 'Šta su gosti rekli ove sezone', sub: 'Najnovije recenzije 9+ na Booking.com, osvježava se svake noći — bez izmjena', all: 'Sve recenzije na Booking.com', ours: 'Naše recenzije' },
  sq: { title: 'Çfarë thanë mysafirët këtë sezon', sub: 'Vlerësimet më të fundit 9+ në Booking.com, përditësohen çdo natë — pa ndryshime', all: 'Të gjitha vlerësimet në Booking.com', ours: 'Vlerësimet tona' },
  de: { title: 'Was Gäste diese Saison sagten', sub: 'Neueste 9+-Bewertungen auf Booking.com, nächtlich aktualisiert — unverändert', all: 'Alle Bewertungen auf Booking.com', ours: 'Unsere Bewertungen' },
  ru: { title: 'Что говорили гости в этом сезоне', sub: 'Свежие отзывы 9+ на Booking.com, обновляются каждую ночь — без правок', all: 'Все отзывы на Booking.com', ours: 'Наши отзывы' },
  pl: { title: 'Co mówili goście w tym sezonie', sub: 'Najnowsze opinie 9+ na Booking.com, aktualizowane co noc — bez zmian', all: 'Wszystkie opinie na Booking.com', ours: 'Nasze opinie' },
  hu: { title: 'Mit mondtak a vendégek idén', sub: 'A legfrissebb 9+ értékelések a Booking.com-on, éjszakánként frissítve — változtatás nélkül', all: 'Összes értékelés a Booking.com-on', ours: 'Értékeléseink' },
};

let shared = null;

const QuoteCard = ({ q, i }) => (
  <article className="snap-start shrink-0 w-[300px] sm:w-[340px] bg-white rounded-2xl border border-slate-200 p-5 flex flex-col gap-3 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-[box-shadow,transform]" data-testid={`booking-quote-${i}`}>
    <div className="flex items-center justify-between">
      <span className="inline-flex items-center justify-center bg-[#003b95] text-white font-bold text-sm rounded-md px-2 py-1" data-testid={`booking-quote-score-${i}`}>{Number(q.score) % 1 === 0 ? Number(q.score).toFixed(0) : q.score}/10</span>
      <Quote size={18} className="text-orange-400" />
    </div>
    {q.title && <h3 className="font-bold text-slate-900 text-base leading-snug">“{q.title}”</h3>}
    <p className="text-sm text-slate-600 leading-relaxed line-clamp-4">{q.text}</p>
    <p className="mt-auto text-xs text-slate-500 font-medium">{q.name}{q.country ? ` (${q.country})` : ''} · {q.date}</p>
  </article>
);

const BookingReviewsStrip = () => {
  const { lang } = useLanguage();
  const [data, setData] = useState(shared);
  const scroller = useRef(null);
  useEffect(() => {
    if (shared) return undefined;
    let alive = true;
    fetch(`${API}/api/public/booking-reviews`).then((r) => r.json()).then((d) => { shared = d; if (alive) setData(d); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  if (!data?.score || !data.quotes?.length) return null;
  const t = T[lang] || T.en;
  const pill = data.pill[PILL_LANG[lang] || 'en'] || data.pill.en;
  const quoteList = data.quotes;
  const cards = quoteList.map((q, i) => <QuoteCard key={q.id || i} q={q} i={i} />);
  const scrollBy = (dir) => scroller.current?.scrollBy({ left: dir * 360, behavior: 'smooth' });
  const prefix = lang === 'en' ? '' : `/${lang}`;
  return (
    <section className="py-12 bg-[#f6f8fc]" data-testid="booking-reviews-strip">
      <div className="container mx-auto px-4">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <a href={hotelInfo.bookingComUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-[#003b95] text-white text-sm font-bold rounded-full px-4 py-1.5 hover:bg-[#002b6d] transition-colors" data-testid="booking-score-pill">{pill}</a>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-3">{t.title}</h2>
            <p className="text-sm text-slate-500 mt-1">{t.sub}</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => scrollBy(-1)} aria-label="Previous" className="w-10 h-10 rounded-full border border-slate-300 bg-white hover:bg-slate-100 inline-flex items-center justify-center transition-colors" data-testid="booking-quotes-prev"><ChevronLeft size={18} /></button>
            <button type="button" onClick={() => scrollBy(1)} aria-label="Next" className="w-10 h-10 rounded-full border border-slate-300 bg-white hover:bg-slate-100 inline-flex items-center justify-center transition-colors" data-testid="booking-quotes-next"><ChevronRight size={18} /></button>
          </div>
        </div>
        <div ref={scroller} className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-3 -mx-4 px-4 [scrollbar-width:thin]" data-testid="booking-quotes-scroller">{cards}</div>
        <div className="flex flex-wrap gap-4 mt-4 text-sm">
          <a href={hotelInfo.bookingComUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[#0071c2] font-semibold hover:underline" data-testid="booking-reviews-all-link">{t.all} <ExternalLink size={14} /></a>
          <a href={`${prefix}/#reviews`} className="text-slate-600 hover:text-orange-600 font-semibold" data-testid="booking-reviews-ours-link">{t.ours} →</a>
        </div>
      </div>
    </section>
  );
};

export default BookingReviewsStrip;
